import pick from 'lodash/pick';
import { storableError } from '../util/errors';
import * as log from '../util/log';
import { BackgroundServiceApi } from '../util/api';
import { transformPaymentMethod } from '../util/stripePaymentMethod';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/paymentMethods/SET_INITIAL_VALUES';

export const CREATE_STRIPE_CUSTOMER_REQUEST = 'app/paymentMethods/CREATE_STRIPE_CUSTOMER_REQUEST';
export const CREATE_STRIPE_CUSTOMER_SUCCESS = 'app/paymentMethods/CREATE_STRIPE_CUSTOMER_SUCCESS';
export const CREATE_STRIPE_CUSTOMER_ERROR = 'app/paymentMethods/CREATE_STRIPE_CUSTOMER_ERROR';

export const ADD_PAYMENT_METHOD_REQUEST = 'app/paymentMethods/ADD_PAYMENT_METHOD_REQUEST';
export const ADD_PAYMENT_METHOD_SUCCESS = 'app/paymentMethods/ADD_PAYMENT_METHOD_SUCCESS';
export const ADD_PAYMENT_METHOD_ERROR = 'app/paymentMethods/ADD_PAYMENT_METHOD_ERROR';

export const DELETE_PAYMENT_METHOD_REQUEST = 'app/paymentMethods/DELETE_PAYMENT_METHOD_REQUEST';
export const DELETE_PAYMENT_METHOD_SUCCESS = 'app/paymentMethods/DELETE_PAYMENT_METHOD_SUCCESS';
export const DELETE_PAYMENT_METHOD_ERROR = 'app/paymentMethods/DELETE_PAYMENT_METHOD_ERROR';

export const FETCH_SAVED_CARDS_REQUEST = 'app/paymentMethods/FETCH_SAVED_CARDS_REQUEST';
export const FETCH_SAVED_CARDS_SUCCESS = 'app/paymentMethods/FETCH_SAVED_CARDS_SUCCESS';
export const FETCH_SAVED_CARDS_ERROR = 'app/paymentMethods/FETCH_SAVED_CARDS_ERROR';

// ================ Reducer ================ //

const initialState = {
  addPaymentMethodInProgress: null,
  addPaymentMethodError: null,
  deletePaymentMethodInProgress: null,
  deletePaymentMethodError: null,
  createStripeCustomerInProgress: null,
  createStripeCustomerError: null,
  stripeCustomer: null,
  // The user's saved cards and their Stripe customer id (the label payment offers them)
  savedCards: [],
  savedCardsCustomerId: null,
  fetchSavedCardsInProgress: false,
  fetchSavedCardsError: null,
};

export default function payoutMethodsPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };
    case CREATE_STRIPE_CUSTOMER_REQUEST:
      return { ...state, createStripeCustomerError: null, createStripeCustomerInProgress: true };
    case CREATE_STRIPE_CUSTOMER_SUCCESS:
      return {
        ...state,
        createStripeCustomerInProgress: false,
        stripeCustomer: payload,
      };
    case CREATE_STRIPE_CUSTOMER_ERROR:
      console.error(payload);
      return {
        ...state,
        createStripeCustomerError: payload,
        createStripeCustomerInProgress: false,
      };
    case ADD_PAYMENT_METHOD_REQUEST:
      return { ...state, addPaymentMethodError: null, addPaymentMethodInProgress: true };
    case ADD_PAYMENT_METHOD_SUCCESS:
      return {
        ...state,
        addPaymentMethodInProgress: false,
        stripeCustomer: payload,
      };
    case ADD_PAYMENT_METHOD_ERROR:
      console.error(payload);
      return {
        ...state,
        addPaymentMethodError: payload,
        addPaymentMethodInProgress: false,
      };

    case DELETE_PAYMENT_METHOD_REQUEST:
      return { ...state, deletePaymentMethodError: null, deletePaymentMethodInProgress: true };
    case DELETE_PAYMENT_METHOD_SUCCESS:
      return {
        ...state,
        deletePaymentMethodInProgress: false,
      };
    case DELETE_PAYMENT_METHOD_ERROR:
      console.error(payload);
      return {
        ...state,
        deletePaymentMethodError: payload,
        deletePaymentMethodInProgress: false,
      };

    case FETCH_SAVED_CARDS_REQUEST:
      return { ...state, fetchSavedCardsInProgress: true, fetchSavedCardsError: null };
    case FETCH_SAVED_CARDS_SUCCESS:
      return {
        ...state,
        fetchSavedCardsInProgress: false,
        savedCards: payload.cards,
        savedCardsCustomerId: payload.customerId,
      };
    case FETCH_SAVED_CARDS_ERROR:
      return {
        ...state,
        fetchSavedCardsInProgress: false,
        fetchSavedCardsError: payload,
        savedCards: [],
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const stripeCustomerCreateRequest = () => ({ type: CREATE_STRIPE_CUSTOMER_REQUEST });

export const stripeCustomerCreateSuccess = stripeCustomer => ({
  type: CREATE_STRIPE_CUSTOMER_SUCCESS,
  payload: stripeCustomer,
});

export const stripeCustomerCreateError = e => ({
  type: CREATE_STRIPE_CUSTOMER_ERROR,
  payload: e,
  error: true,
});

export const addPaymentMethodRequest = () => ({ type: ADD_PAYMENT_METHOD_REQUEST });

export const addPaymentMethodSuccess = stripeCustomer => ({
  type: ADD_PAYMENT_METHOD_SUCCESS,
  payload: stripeCustomer,
});

export const addPaymentMethodError = e => ({
  type: ADD_PAYMENT_METHOD_ERROR,
  payload: e,
  error: true,
});

export const deletePaymentMethodRequest = () => ({ type: DELETE_PAYMENT_METHOD_REQUEST });

export const deletePaymentMethodSuccess = stripeCustomer => ({
  type: DELETE_PAYMENT_METHOD_SUCCESS,
  payload: stripeCustomer,
});

export const deletePaymentMethodError = e => ({
  type: DELETE_PAYMENT_METHOD_ERROR,
  payload: e,
  error: true,
});

const fetchSavedCardsRequest = () => ({ type: FETCH_SAVED_CARDS_REQUEST });
const fetchSavedCardsSuccess = (customerId, cards) => ({
  type: FETCH_SAVED_CARDS_SUCCESS,
  payload: { customerId, cards },
});
const fetchSavedCardsError = e => ({ type: FETCH_SAVED_CARDS_ERROR, payload: e, error: true });

// ================ Thunks ================ //

/**
 * The user's saved cards, from their Stripe customer (none without one). A failure
 * leaves none: the payment then asks for a card.
 */
export const fetchSavedCards = customerId => dispatch => {
  if (!customerId) {
    dispatch(fetchSavedCardsSuccess(null, []));
    return Promise.resolve([]);
  }
  dispatch(fetchSavedCardsRequest());
  return BackgroundServiceApi.listPaymentMethods(customerId)
    .then(response => {
      const cards = response?.paymentMethods || [];
      dispatch(fetchSavedCardsSuccess(customerId, cards));
      return cards;
    })
    .catch(e => {
      log.error(storableError(e), 'fetch-saved-cards-failed');
      dispatch(fetchSavedCardsError(storableError(e)));
      return [];
    });
};

/**
 * Keeps a card just paid with for later: on the user's Stripe customer, made for
 * them if they have none. The payment is done either way, so a failure is only logged.
 */
export const saveCardForLater = (paymentMethodId, customerId) => dispatch =>
  (customerId
    ? Promise.resolve(customerId)
    : BackgroundServiceApi.createOrRetrieveCustomer({}).then(response => response?.customerId)
  )
    .then(id => {
      if (!id) return null;
      return BackgroundServiceApi.attachPaymentMethod({ paymentMethodId, customerId: id }).then(
        () => dispatch(fetchSavedCards(id))
      );
    })
    .catch(e => {
      log.error(storableError(e), 'save-card-for-later-failed');
    });

/**
 * The buyer's Stripe customer id, made for them if they have none (checkout, when a
 * card is to be saved). Resolves with null when it can't be had: the payment goes on
 * without saving the card.
 */
export const retrieveStripeCustomerId = () => () =>
  BackgroundServiceApi.createOrRetrieveCustomer({})
    .then(response => response?.customerId || null)
    .catch(e => {
      log.error(storableError(e), 'retrieve-stripe-customer-failed');
      return null;
    });

export const createStripeCustomer = stripePaymentMethodId => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerCreateRequest());

  // Create customer in our TradMarket API
  return BackgroundServiceApi.createOrRetrieveCustomer({})
    .then(response => {
      const { customerId, customer } = response;

      // Attach the payment method to the customer
      return BackgroundServiceApi.attachPaymentMethod({
        customerId,
        paymentMethodId: stripePaymentMethodId,
      }).then(() => {
        // Store customer ID in user's protectedData
        return sdk.currentUser
          .updateProfile(
            {
              protectedData: {
                stripeCustomerId: customerId,
              },
            },
            { expand: true }
          )
          .then(() => {
            // Fetch payment methods to get complete data
            return BackgroundServiceApi.listPaymentMethods(customerId).then(pmResponse => {
              // Transform response to match expected structure
              const stripeCustomer = {
                id: customerId,
                attributes: {
                  stripeCustomerId: customerId,
                },
                defaultPaymentMethod: transformPaymentMethod(pmResponse.paymentMethods?.[0]),
              };
              dispatch(stripeCustomerCreateSuccess(stripeCustomer));
              return stripeCustomer;
            });
          });
      });
    })
    .catch(e => {
      log.error(storableError(e), 'create-stripe-user-failed');
      dispatch(stripeCustomerCreateError(storableError(e)));
    });
};

export const addPaymentMethod = stripePaymentMethodId => (dispatch, getState, sdk) => {
  dispatch(addPaymentMethodRequest());

  // Get customer ID from current user's protectedData
  return sdk.currentUser
    .show()
    .then(userResponse => {
      const customerId = userResponse.data.data.attributes.profile.protectedData?.stripeCustomerId;

      if (!customerId) {
        throw new Error('No Stripe customer found for user');
      }

      // Attach payment method
      return BackgroundServiceApi.attachPaymentMethod({
        customerId,
        paymentMethodId: stripePaymentMethodId,
      }).then(() => {
        // Fetch updated payment methods list
        return BackgroundServiceApi.listPaymentMethods(customerId).then(pmResponse => {
          const stripeCustomer = {
            id: customerId,
            attributes: {
              stripeCustomerId: customerId,
            },
            defaultPaymentMethod: transformPaymentMethod(pmResponse.paymentMethods?.[0]),
          };
          dispatch(addPaymentMethodSuccess(stripeCustomer));
          return stripeCustomer;
        });
      });
    })
    .catch(e => {
      log.error(storableError(e), 'add-payment-method-failed');
      dispatch(addPaymentMethodError(storableError(e)));
    });
};

export const deletePaymentMethod = paymentMethodId => (dispatch, getState, sdk) => {
  dispatch(deletePaymentMethodRequest());

  // If payment method ID not provided, get it from Redux state
  const getPaymentMethodId = paymentMethodId
    ? Promise.resolve(paymentMethodId)
    : (() => {
        const state = getState();
        const stripeCustomer = state.paymentMethods?.stripeCustomer;
        const defaultPM = stripeCustomer?.defaultPaymentMethod;

        if (!defaultPM || !defaultPM.id) {
          return Promise.reject(new Error('No payment method to delete'));
        }
        return Promise.resolve(defaultPM.id);
      })();

  return getPaymentMethodId
    .then(pmId => {
      return BackgroundServiceApi.deletePaymentMethod(pmId).then(() => {
        // Fetch updated user to get current payment methods
        return sdk.currentUser.show().then(userResponse => {
          const customerId =
            userResponse.data.data.attributes.profile.protectedData?.stripeCustomerId;

          if (customerId) {
            // Fetch remaining payment methods
            return BackgroundServiceApi.listPaymentMethods(customerId).then(pmResponse => {
              const stripeCustomer = {
                id: customerId,
                attributes: {
                  stripeCustomerId: customerId,
                },
                defaultPaymentMethod: transformPaymentMethod(pmResponse.paymentMethods?.[0]),
              };
              dispatch(deletePaymentMethodSuccess(stripeCustomer));
              return stripeCustomer;
            });
          } else {
            // No customer ID, just return empty
            dispatch(deletePaymentMethodSuccess({}));
            return {};
          }
        });
      });
    })
    .catch(e => {
      log.error(storableError(e), 'delete-payment-method-failed');
      dispatch(deletePaymentMethodError(storableError(e)));
    });
};

export const updatePaymentMethod = stripePaymentMethodId => (dispatch, getState, sdk) => {
  return dispatch(deletePaymentMethod())
    .then(() => {
      return dispatch(addPaymentMethod(stripePaymentMethodId));
    })
    .catch(e => {
      log.error(storableError(e), 'updating-payment-method-failed');
    });
};

// This function helps to choose correct thunk function
export const savePaymentMethod = (stripeCustomer, stripePaymentMethodId) => (
  dispatch,
  getState,
  sdk
) => {
  const hasAlreadyDefaultPaymentMethod =
    stripeCustomer && stripeCustomer.defaultPaymentMethod && stripeCustomer.defaultPaymentMethod.id;

  const savePromise =
    !stripeCustomer || !stripeCustomer.id
      ? dispatch(createStripeCustomer(stripePaymentMethodId))
      : hasAlreadyDefaultPaymentMethod
      ? dispatch(updatePaymentMethod(stripePaymentMethodId))
      : dispatch(addPaymentMethod(stripePaymentMethodId));

  return savePromise
    .then(response => {
      const {
        createStripeCustomerError,
        addPaymentMethodError,
        deletePaymentMethodError,
      } = getState().paymentMethods;

      // If there are any errors, return those errors
      if (createStripeCustomerError || addPaymentMethodError || deletePaymentMethodError) {
        return {
          errors: { createStripeCustomerError, addPaymentMethodError, deletePaymentMethodError },
        };
      }
      return response;
    })
    .catch(e => {
      // errors are already catched in other thunk functions.
    });
};
